import * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AddButton from "./AddButton";
import RemoveButton from "./RemoveButton";
import { Tag, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Reusable categorized skills input component
 * Manages multiple skill categories, each with its own tag list
 * Uses shadcn/ui components for modern design
 * 
 * @param {Object} skills - Object with category keys and arrays of skill strings
 *                          e.g., { science_subjects: ['Biology', 'Chemistry'], teaching_methods: ['Inquiry-based'] }
 * @param {Function} onChange - Callback when skills change: (skills) => void
 * @param {Function} onClear - Optional callback to clear all skills
 * @param {string} clearLabel - Optional label for clear action (default: "Clear all")
 * @param {Array} categories - Array of category configs: [{ key: string, label: string, placeholder?: string }]
 * @param {string} className - Additional CSS classes
 */
function CategorizedSkillsInput({
  skills = {},
  onChange,
  onClear,
  clearLabel = "Clear all",
  categories = [],
  className = "",
}) {
  const [newSkillDrafts, setNewSkillDrafts] = React.useState({});

  const getCategoryList = React.useCallback(
    (categoryKey) => (Array.isArray(skills?.[categoryKey]) ? skills[categoryKey] : []),
    [skills]
  );

  // Initialize drafts for all categories
  React.useEffect(() => {
    const drafts = { ...newSkillDrafts };
    categories.forEach(({ key }) => {
      if (!(key in drafts)) {
        drafts[key] = "";
      }
    });
    setNewSkillDrafts(drafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length]);

  const handleAddSkill = (categoryKey) => {
    const skillValue = newSkillDrafts[categoryKey]?.trim();
    if (!skillValue) return;

    const currentSkills = getCategoryList(categoryKey);
    const updatedSkills = {
      ...skills,
      [categoryKey]: [...currentSkills, skillValue],
    };

    if (onChange) {
      onChange(updatedSkills);
    }

    // Clear the input for this category
    setNewSkillDrafts((prev) => ({ ...prev, [categoryKey]: "" }));
  };

  const handleRemoveSkill = (categoryKey, index) => {
    const currentSkills = getCategoryList(categoryKey);
    const updatedSkills = {
      ...skills,
      [categoryKey]: currentSkills.filter((_, i) => i !== index),
    };

    if (onChange) {
      onChange(updatedSkills);
    }
  };

  const handleInputChange = (categoryKey, value) => {
    setNewSkillDrafts((prev) => ({ ...prev, [categoryKey]: value }));
  };

  const handleKeyPress = (categoryKey, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSkill(categoryKey);
    }
  };

  return (
    <div className={cn("space-y-6", className)}>
      {onClear && (
        <div className="flex items-center justify-between gap-4">
          <Label className="text-sm font-medium">Clear All Skills</Label>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="gap-2"
            onClick={onClear}
          >
            <Trash2 className="h-4 w-4" />
            {clearLabel}
          </Button>
        </div>
      )}

      {categories.map(({ key, label, placeholder }) => {
        const categorySkills = getCategoryList(key);
        const draftValue = newSkillDrafts[key] || "";

        return (
          <div key={key} className="space-y-2">
            <Label className="text-sm font-medium">{label}</Label>
            
            {categorySkills.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {categorySkills.map((skill, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="gap-2 pr-1"
                  >
                    {skill}
                    <RemoveButton
                      onClick={() => handleRemoveSkill(key, index)}
                      ariaLabel={`Remove ${label.toLowerCase()} ${index + 1}`}
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5 hover:bg-destructive/10 hover:text-destructive"
                      useXIcon={true}
                    />
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="text"
                  value={draftValue}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  onKeyPress={(e) => handleKeyPress(key, e)}
                  placeholder={placeholder || `Add ${label.toLowerCase()}...`}
                  className="pl-9 placeholder:text-muted-foreground/60 placeholder:italic"
                />
              </div>
              <AddButton
                onClick={() => handleAddSkill(key)}
                variant="secondary"
                size="default"
              >
                Add
              </AddButton>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CategorizedSkillsInput;
